import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { Store } from "@ngrx/store";
import { ElementType, FormatType, LineCap, LineJoin, NgWhiteboardComponent, NgWhiteboardService, RelativeTransformData, ToolType, WhiteboardElement, WhiteboardOptions } from "ng-whiteboard";
import { Subscription } from "rxjs";
import * as appActions from "src/app//store/actions/app.actions";
import { AppHelper } from "src/app/helpers/app.helper";
import { FileSenderHelper } from "src/app/helpers/fileSender.helper";
import { ResizedEvent } from "src/app/helpers/resized.event";
import { INITIAL_TRANSFORM_DATA, WhiteboardHelper } from "src/app/helpers/whiteboard.helper";
import { CommandEvent, SharedWhiteboard } from "src/app/models/commandEvent";
import { DrawingProperties } from "src/app/models/whiteboard";
import { AppConfigService } from "src/app/services/app.config.service";
import { I18Service } from "src/app/services/i18.service";
import { ImageAnalyzer } from "src/app/services/imageAnalyzer.service";
import { NotificationService } from "src/app/services/notification.service";
import { SessionService } from "src/app/services/session.service";
import * as sessionStatusActions from "src/app/store/actions/sessionStatus.actions";
import { RootState } from "src/app/store/root.state";


const STANDARD_HEIGHT = 600; // altezza standard in pixel


type ColorProperty = 'fill' | 'strokeColor' | 'backgroundColor';

export type CanvasData = {
    id: string;
    image: string;
    width: number;
    height: number;
}

@Component({
    selector: 'app-whiteboard',
    templateUrl: './whiteboard.component.html',
    styleUrls: ['./whiteboard.component.scss']
})

export class WhiteboardComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {

    @Input() closedExternally = false;

    undoRedoStackSizes = {undoSize:0, redoSize:0};
    private whiteboardInitialized = false;
    private pendingBaselineCapture = false;
    hidePropertiesButton = false;
    // Layout properties
    toolbarPosition: 'top' | 'left' = 'left';
    containerAspectRatio = 1;

    // Layout: confronto area immagine per decidere toolbar top vs left
    private readonly TB_LEFT_W = 75;   // stima generosa toolbar verticale (worst case 2 colonne)
    private readonly TB_TOP_H = 100;   // stima generosa toolbar orizzontale (worst case 2 righe)
    private readonly AREA_GAIN_THRESHOLD = 0.05; // cambio posizione solo se guadagno area > 5%
    private resizeDebounceTimer?: any;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['closedExternally']?.currentValue === true) {
            console.log(`WhiteboardComponent ngOnChanges - closedExternally is true, closing whiteboard`);
            this.config.setWhiteboardHasUnsavedChanges(false);
            this.closeWhiteboard();
        }
    }

    @Input('canvasData') set canvasData( value: CanvasData | undefined) {
        if(!value) return;
        this._canvasData = value;
        console.log(`whiteboard.component set input canvasData`,value);
        //this.updateOptions({ fontSize: this.getFontSizeForViewBox(this.config.whiteboard.properties.fontSize, this._canvasData?.height || STANDARD_HEIGHT)});
        this.addCanvasImage(value);
    };

    //@Input() readonly = AppConfigService.role == PartecipantRoles.SecondaryClient || this.config.AS?.whiteboardSharedEnabled; //si potrebbe prendere da AS

    @Output() close = new EventEmitter<boolean>();
    @Output() opened = new EventEmitter<boolean>();
//    @ViewChild('analyze',{static: false}) analazydiv!: ElementRef;
    @ViewChild(NgWhiteboardComponent, {static: false}) whiteboardComponent! : NgWhiteboardComponent
    // @ViewChild('whiteboardContainer', {static: false}) whiteboardContainer!: ElementRef;

    _canvasData?: CanvasData;
    imageSizeRatio = {width: 640, height: 480, ratio: 1};

    tools = [
        {value: ToolType.Select, icon:'arrow_selector_tool', forEdit: true},
        {value: ToolType.Hand, icon:'pan_tool', forEdit: false},
        {value: ToolType.ZoomIn, icon:'screenshot_region', forEdit: false},
        // {value: ToolType.ZoomOut, icon:'zoom_out'},
        {value: ToolType.Pen, icon:'draw', forEdit: true},
        {value: ToolType.Line, icon:'diagonal_line', forEdit: true},
        {value: ToolType.Arrow, icon:'north_west', forEdit: true},
        {value: ToolType.Text, icon:'text_fields', forEdit: true},
        {value: ToolType.Rectangle, icon:'rectangle', forEdit: true},
        {value: ToolType.Ellipse, icon:'circle', forEdit: true},
        {value: ToolType.Eraser, icon:'ink_eraser', forEdit: true}
    ];

    toolSelected = this.config.agencySettings.whiteboard.canEdit ? this.tools.find(t=>t.value == ToolType.Select)! : this.tools.find(t=>t.value == ToolType.ZoomIn)!;
    maxZoom = AppConfigService.ZOOM_LEVELS[AppConfigService.ZOOM_LEVELS.length - 1];
    selectedElements: WhiteboardElement[] = [];
    // realtimeEnabled = false;
    isToggling = false;
    addingImage=false;
    showWhiteboard = false;

    canvasContainerWidth = 0;
    canvasContainerHeight = 0;
    imageElement: WhiteboardElement | undefined = undefined;


    private _subscriptionList: Subscription[] = [];
    
    options: WhiteboardOptions = {
        drawingEnabled: !this.config.whiteboardReadonly,
        strokeColor: this.config.whiteboardProperties.strokeColor,
        strokeWidth: this.config.whiteboardProperties.strokeWidth,
        fontFamily: this.config.whiteboardProperties.fontFamily,
        fontSize: this.config.whiteboardProperties.fontSize,
        fill: 'transparent',
        // backgroundColor: '#F8F9FA',
        backgroundColor: 'white',
        canvasHeight: 600,
        canvasWidth: 800,
        dasharray: WhiteboardHelper.lineStyleToDashArray(this.config.whiteboardProperties.lineStyle, this.config.whiteboardProperties.strokeWidth),
        lineJoin: LineJoin.Miter,
        lineCap: LineCap.Butt,
        zoomBase: 1,
        fullScreen: false,
        center: false,
        enableGrid: false,
        gridSize: 20,
        // gridColor: '#000000',
        x: 0,
        y: 0

    };




    constructor(
        public config: AppConfigService,
        private store: Store<RootState>,
        private sessionService: SessionService,
        public ngWhiteboardService: NgWhiteboardService,
        public imageAnalizer: ImageAnalyzer,
        private elementRef: ElementRef) {
      }

    private getFontSizeForViewBox(desiredPixelSize: number, viewBoxHeight: number): number {
        return desiredPixelSize;
        // Calcola la dimensione del font in base all'altezza del viewBox
        // Assumendo che vuoi che il font appaia come X pixel su schermo
        const standardHeight = STANDARD_HEIGHT; // altezza standard in pixel
        const fs=(desiredPixelSize * viewBoxHeight) / standardHeight;
        console.log(`getFontSizeForViewBox: desiredPixelSize=${desiredPixelSize}, viewBoxHeight=${viewBoxHeight}, calculated fs=${fs}`);
        return fs;
    }

    private fillWhiteboardData(elements: WhiteboardElement[]) {
        if (!this.imageElement) {
            return
        }

        if (this.config.whiteboardData.find(z => z.type == ElementType.Image) == undefined) {
            console.log(`fillWhiteboardData adding image first`);
            this.config.setWhiteboardData( [this.imageElement!, ...elements] );
        } else {
            this.config.setWhiteboardData(this.config.whiteboardData.slice(0,1).concat(elements));
        }
        console.log(`fillWhiteboardData elements [${elements.map(e => e.type)}] after image!`);
    }  

    ngAfterViewInit(): void {
        // console.log(this.tools);
        // console.log(Object.keys(ToolType));
    }
    
    ngOnDestroy(): void {
        console.log(`WhiteboardComponent ngOnDestroy`);
        this.imageElement = undefined;
        this._canvasData = undefined;

        this._subscriptionList.forEach((subscription) => subscription?.unsubscribe());
        if (this.resizeDebounceTimer) {
            clearTimeout(this.resizeDebounceTimer);
        }
        this.whiteboardComponent.ngOnDestroy();
    }
    ngOnInit(): void {  
        // this._subscriptionList.push(
        //     this.store.select(whiteboardData).subscribe((whiteboard) => {
        //         console.log(`whiteboard ngOnInit - elements [${whiteboard?.data?.elements.map(e => e.type)}]`, whiteboard);
        //         // this.readonly = AppConfigService.role == PartecipantRoles.SecondaryClient || this.config.AS?.whiteboardSharedEnabled;
        //         if (!whiteboard) return;
        //         // if (whiteboard.data?.zoom !== undefined || whiteboard.data?.canvasPosition ){
        //         //     this.whiteboardService.setRelativeTransform({
        //         //         zoomRelative: whiteboard.data?.zoom || 1, 
        //         //         x: whiteboard.data?.canvasPosition.x || 0, 
        //         //         y: whiteboard.data?.canvasPosition.y || 0
        //         //     });
        //         // }
        //         if (whiteboard?.data?.elements) {
        //             this.fillWhiteboardData(whiteboard.data.elements);
        //         }
                
        //     })
        // );      
    }
    
    closeOnSave = false;
    sendImage(){
        this.closeOnSave = false;
        this.ngWhiteboardService.save(FormatType.Base64, 'xyz', true);
    }
    sendImageAndCloseWhiteboard(){
        this.closeOnSave = true;
        this.ngWhiteboardService.save(FormatType.Base64, 'xyz', true);
    }
    onSave(screenCapture: string) {
        this.store.dispatch(appActions.loadingMessage({ message: I18Service.get("Whiteboard.SendingImage") })); //send image
        const now = new Date();
        const formattedDateTime = now.toISOString().replace(":",".");
        const fileName = `image_${formattedDateTime}.png`;

        FileSenderHelper.sendWhiteboardImage(screenCapture, fileName, this.config.partecipantId).then( (b:boolean) => {
            console.log(`[WB-SAVE] onSave - image sent, markAsSaved(undoSize=${this.undoRedoStackSizes.undoSize})`);
            this.config.markWhiteboardAsSaved(this.undoRedoStackSizes.undoSize);

            if (this.closeOnSave) {
                this.closeWhiteboard();
                this.closeOnSave = false;
            }
        })
        .finally(() => {this.store.dispatch(appActions.loadingMessage({ message: '' }))}); //send image complete
    }
    

    closeWhiteboard(){
        
        if(this.config.whiteboardSharingEnabled) {
            const val = this.sharedWhiteboardData(false);
            this.store.dispatch(sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.EnableSharedWhiteboard, val, toIds: appActions.SEND_TO_ALL }))
        }

        this.close.emit(true);
        this._canvasData = undefined;
        this.ngWhiteboardService.clear(); //???
    }

    private sharedWhiteboardData(enabled: boolean): SharedWhiteboard {
        const val: SharedWhiteboard = {
            enabled,
            who: this.config.partecipantId
        }
        return val;
    }
    
    private firstResize = true;

    onResized(event: ResizedEvent) {
        console.log('********************* whiteboard resized event', event)
        const newWidth = event.newRect.width -8;
        const newHeight = event.newRect.height -8;

        // Sempre resize della whiteboard per mantenere l'immagine corretta
        this.resizeWhiteboard(newWidth, newHeight);

        if (this.firstResize) {
            // Al primo resize calcola subito per evitare il flash della toolbar
            this.firstResize = false;
            this.calculateOptimalLayout(newWidth, newHeight);
            return;
        }

        // Debounce per evitare calcoli continui durante il trascinamento
        clearTimeout(this.resizeDebounceTimer);
        this.resizeDebounceTimer = setTimeout(() => {
            this.calculateOptimalLayout(newWidth, newHeight);
        }, 150); // 150ms di debounce
    }

    onDataChange(event: WhiteboardElement[]){ // evitare che una sequenza di undo elimini l'immagine di sfondo
        if(this.addingImage) return;
        console.log(`[TB-DEBUG] onDataChange - ${event.length} elements [${event.map(e => e.type)}] - undoSize: ${this.undoRedoStackSizes.undoSize}`);
        
        if(event && event.length == 0 ){
            // if(this._canvasData ){
            //     this.addCanvasImage(this._canvasData); // reinserisce l'immagine evidentemente cancellata
            // }
            return;
        }


        // const lastElement = event.slice(-1)[0]; //ultimo elemento in lista
        // if (lastElement && lastElement.type == ElementType.Text) {
        //     lastElement.options.fill = this.strokeColor;
        // }
        const elementsWithoutImage = event.filter(z => z.type != ElementType.Image);
        this.config.setWhiteboardElements(elementsWithoutImage);
        // unsavedChanges viene valutato in onUndoRedoStackChanged (ha undoSize aggiornato)


        if (this.config.whiteboardSharingEnabled) {
            
            const val = WhiteboardHelper.zipWhiteboardElements(elementsWithoutImage);
            if (val.length > 640 * 1024) {
                NotificationService.popupError({msg: `Too many elements, stop the sharing!`});
            } else {
                this.store.dispatch(sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.WhiteboardElements, val, toIds: appActions.SEND_TO_ALL }));
            }
            this.store.dispatch(sessionStatusActions.cmd_dispatch({ 
                cmd: CommandEvent.WhiteboardRelativeTransform, 
                val: this.config.whiteboardTransformData, 
                toIds: appActions.SEND_TO_ALL}))
        }


        if(!this.config.whiteboardIsEditing){ return;}
        //autoselect #93414
        if (event.length > this.previousDataLength) {
          const newElement = event[event.length - 1];

          if (newElement.type !== ElementType.Image) {
              this.ngWhiteboardService.selectElements([newElement]);
          }
        }
        this.previousDataLength = event.length;
    }
    private previousDataLength = 0;

    onElementsAdded(event: WhiteboardElement[]) {
        console.log(`xyzw onElementsAdded - ${event.length} elements [${event.map(e => e.type)}] - undoSize: ${this.undoRedoStackSizes.undoSize}`);

        const imageElement = event.find(z => z.type == ElementType.Image);
        if (imageElement){
            // this.config.setWhiteboardElements([imageElement]);
            if(!this.imageElement)
            {
                console.log(`whiteboard onElementsAdded set ImageElement`);
                this.imageElement = event.find(z => z.type == ElementType.Image);
            }
        }
    }
    onElementsUpdated(elements: WhiteboardElement[]) {
        console.log(`xyzw onElementsUpdated - ${elements.length} elements [${elements.map(e => e.type)}] - undoSize: ${this.undoRedoStackSizes.undoSize}`);
    }

    onElementsSelected(elements: WhiteboardElement[]){

        console.log(`xyzw whiteboard onElementsSelected - ${elements.length} elements [${elements.map(e => e.type)}]`);
        elements.forEach((el: WhiteboardElement) => {
            if(el != null &&  el.type == ElementType.Image){
                this.ngWhiteboardService.deselectElement(el);
            }
        });

        // Controlla se la selezione è effettivamente cambiata
        const selectionChanged = elements.length !== this.selectedElements.length ||
            elements.some((el, index) => el.id !== this.selectedElements[index]?.id);

        this.selectedElements = elements;

        // Aggiorna immediatamente - il controllo del colore in colorChange() previene undo spurii
        this.updatePropertiesFromSelection(selectionChanged, elements);
    }
    
    private isUpdatingColorFromSelection: boolean = false;
  private updatePropertiesFromSelection(selectionChanged: boolean, elements: WhiteboardElement[]) {
      console.log(`xyzw 🔧 updatePropertiesFromSelection`);
      console.log(`xyzw    selectionChanged: ${selectionChanged}`);
      console.log(`xyzw    elements.length: ${elements.length}`);

      if (selectionChanged && elements.length === 1) {
          const element = elements[0];

          console.log(`xyzw    📝 Aggiornando properties per elemento: ${element.type} - ${element.id}`);
          console.log(`xyzw    📝 Colore elemento: ${element.type === ElementType.Text ? element.style.color : element.style.strokeColor}`);
          console.log(`xyzw    📝 currentStrokeColor PRIMA: ${this.currentStrokeColor}`);

          // ✅ Attiva il flag PRIMA di aggiornare currentStrokeColor
          this.isUpdatingColorFromSelection = true;
          console.log(`xyzw    🚩 Flag ATTIVATO`);

          if (element.type === ElementType.Text) {
              this.drawingProperties = {
                  ...this.drawingProperties,
                  fontFamily: element.style.fontFamily || this.drawingProperties.fontFamily,
                  fontSize: element.style.fontSize || this.drawingProperties.fontSize,
                  fontStyle: element.style.fontStyle || this.drawingProperties.fontStyle,
                  fontWeight: element.style.fontWeight || this.drawingProperties.fontWeight,
                  strokeColor: element.style.color || this.drawingProperties.strokeColor
              };
              this.currentStrokeColor = element.style.color || this.drawingProperties.strokeColor || '#000000';
          } else {
              this.drawingProperties = {
                  ...this.drawingProperties,
                  strokeColor: element.style.strokeColor || this.drawingProperties.strokeColor,
                  strokeWidth: element.style.strokeWidth || this.drawingProperties.strokeWidth,
                  lineStyle: WhiteboardHelper.getLineStyleFromDashArray(
                      element.style.dasharray || '',
                      this.drawingProperties.strokeWidth
                  )
              };
              this.currentStrokeColor = element.style.strokeColor || this.currentStrokeColor;
          }

          console.log(`xyzw    📝 currentStrokeColor DOPO: ${this.currentStrokeColor}`);

          // ✅ Resetta il flag dopo che Angular ha processato il cambio
          setTimeout(() => {
              this.isUpdatingColorFromSelection = false;
              console.log(`xyzw    🏁 Flag DISATTIVATO`);
          }, 50);
      } else {
          console.log(`xyzw    ⏭️ Nessun aggiornamento (multi-selezione o selezione non cambiata)`);
      }
  }

    onElementsDeleted(){ // evitare che con gomma si cancelli l'immagine di sfondo [evitato da lib .d6]
        console.log(`whiteboard onElementsDeleted `);
    }

    onImageAdded(event: File) {
        const debugStr = event.toString().substring(23, 50)
        console.log(`whiteboard onImageAdded ${debugStr} ${event.toString().length}bytes`);
        this.addingImage = false;
        this.whiteboardInitialized = false;
        setTimeout( () => {
            this.showWhiteboard = true;
            this.ReplaceElements(this.config.whiteboardElements || []);

            const sb = this.config.whiteboardTransformData;
            setTimeout(()=>this.ngWhiteboardService.applyRelativeTransform(sb!));

            this.opened.emit(true);

            // Cattura il baseline undoSize dopo che tutti gli eventi di init sono terminati
            setTimeout(() => {
                this.whiteboardInitialized = true;
                if (!this.config.whiteboardOpenedFromVideo) {
                    console.log(`[WB-SAVE] onImageAdded init complete (image mode) - markAsSaved(undoSize=${this.undoRedoStackSizes.undoSize})`);
                    this.config.markWhiteboardAsSaved(this.undoRedoStackSizes.undoSize);
                } else {
                    console.log(`[WB-SAVE] onImageAdded init complete (video mode) - unsaved=true`);
                    this.config.setWhiteboardHasUnsavedChanges(true);
                }
            }, 300);
        });
    }

    onRelativeTransformChange(transform: RelativeTransformData) {
        console.log(`whiteboard onRelativeTransformChange ${JSON.stringify(transform)}`);
        this.config.setWhiteboardRelativeTransform(transform);

        if (this.config.whiteboardSharingEnabled) {
            this.store.dispatch(sessionStatusActions.cmd_dispatch({
                cmd: CommandEvent.WhiteboardRelativeTransform,
                val: this.config.whiteboardTransformData,
                toIds: appActions.SEND_TO_ALL}))
        }
        // unsavedChanges viene valutato in onUndoRedoStackChanged (ha undoSize aggiornato)
    }
    // onPanChange(transform: PanTransformData) {
    //     this.config.setWhiteboardElementsTranslations( transform.elementsTranslation );
    //     if (this.config.whiteboard.sharingEnabled) {
    //         console.log(`whiteboard onPanChange ${JSON.stringify(transform)}`);
    //         this.store.dispatch(sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.WhiteboardPan, val: transform, toIds: appActions.SEND_TO_ALL}))
    //     }
    // }
    onConfigChanged( x: WhiteboardOptions){
        console.log(`[TB-DEBUG] onConfigChanged zoomBase=${x.zoomBase?.toFixed(4)} containerW=${x.containerWidth} containerH=${x.containerHeight}`);
    }
    onUndoRedoStackChanged( d: {undoSize: number, redoSize: number}){
        console.log(`[TB-DEBUG] onUndoRedoStackChanged: undoSize=${d.undoSize} (was ${this.undoRedoStackSizes.undoSize}), redoSize=${d.redoSize}, toolbarPos=${this.toolbarPosition}`);
        this.undoRedoStackSizes = d;
        this.evaluateUnsavedChanges();
    }

    private evaluateUnsavedChanges() {
        if (this.config.whiteboardReadonly || !this.whiteboardInitialized) {
            console.log(`[WB-SAVE] evaluateUnsavedChanges SKIP: readonly=${this.config.whiteboardReadonly}, initialized=${this.whiteboardInitialized}`);
            return;
        }

        // Dopo un control transfer (inherited=false), la libreria può emettere un undo event
        // in ritardo (quando drawingEnabled passa a true). Invece di valutare, ricatturiamo il baseline.
        if (this.pendingBaselineCapture) {
            console.log(`[WB-SAVE] evaluateUnsavedChanges: pendingBaselineCapture → markAsSaved(undoSize=${this.undoRedoStackSizes.undoSize})`);
            this.config.markWhiteboardAsSaved(this.undoRedoStackSizes.undoSize);
            this.pendingBaselineCapture = false;
            return;
        }

        // Video mode: bottone save sempre abilitato
        if (this.config.whiteboardOpenedFromVideo) {
            console.log(`[WB-SAVE] evaluateUnsavedChanges: VIDEO mode → unsaved=true`);
            this.config.setWhiteboardHasUnsavedChanges(true);
            return;
        }

        const savedUndoSize = this.config.whiteboardSavedUndoSize;
        if (savedUndoSize < 0) {
            // Sentinel: mai salvato o ereditato unsaved da control transfer → serve save esplicito
            console.log(`[WB-SAVE] evaluateUnsavedChanges: sentinel (savedUndoSize=${savedUndoSize}) → unsaved=true`);
            this.config.setWhiteboardHasUnsavedChanges(true);
            return;
        }

        // Confronto undoSize corrente con il punto di salvataggio
        const isAtSavedState = this.undoRedoStackSizes.undoSize === savedUndoSize;
        console.log(`[WB-SAVE] evaluateUnsavedChanges: undoSize=${this.undoRedoStackSizes.undoSize} savedUndoSize=${savedUndoSize} → unsaved=${!isAtSavedState}`);
        this.config.setWhiteboardHasUnsavedChanges(!isAtSavedState);
    }

    undo() {
        // si può evitare l'undo se c'e 1 elemento => è l'immagine di sfondo

        if (this.config.whiteboardElements?.length || this.config.whiteboardTransformData) {
            this.ngWhiteboardService.undo();
        }
    }
    redo() {
        if(this.undoRedoStackSizes.redoSize > 0) {
            this.ngWhiteboardService.redo();
        }
    }
    reset() {

        if (this.toolSelected.value == ToolType.Hand) {
            this.toolSelected = this.tools.find(t=>t.value == ToolType.Select)!;
        }

        this.config.setWhiteboardRelativeTransform(INITIAL_TRANSFORM_DATA);

        this.ngWhiteboardService.setRelativeTransform(this.config.whiteboardTransformData);

        this.ngWhiteboardService.clear(true);
        this.ngWhiteboardService.addImage(this._canvasData!.image);
        if (this.config.whiteboardSharingEnabled) {
            this.store.dispatch(sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.WhiteboardClear, val:'', toIds: appActions.SEND_TO_ALL }))
        }

        // Re-cattura baseline dopo che il reset completa e l'undo stack si stabilizza
        this.whiteboardInitialized = false;
        this.config.setWhiteboardHasUnsavedChanges(false);
        setTimeout(() => {
            this.whiteboardInitialized = true;
            if (!this.config.whiteboardOpenedFromVideo) {
                console.log(`[WB-SAVE] reset complete - markAsSaved(undoSize=${this.undoRedoStackSizes.undoSize})`);
                this.config.markWhiteboardAsSaved(this.undoRedoStackSizes.undoSize);
            }
        }, 300);
    }
    cleared(){}
    isToolDisabled(tool: {value: ToolType, icon: string}): boolean {
        if(tool.value != ToolType.Hand) return false;

        return (this.config.whiteboardTransformData?.zoomRelative || 1) == 1;
    }

    public analyzeImage(event: any){
        if(this._canvasData){
            var a = this.imageAnalizer.analyze(this._canvasData.image).subscribe({
                next: async (resp)=> {
                    console.log(`Whiteboard - analyzeImage`,resp);

                    const ratio = this.imageSizeRatio.ratio;
                    if(resp.objects && resp.objects.values.length){
                        for(const o of resp.objects.values){
                            
                            const start = {offsetX: o.boundingBox.x * ratio, offsetY: o.boundingBox.y *ratio}
                            const end = {offsetX: (o.boundingBox.x + o.boundingBox.width)*ratio, offsetY: (o.boundingBox.y + o.boundingBox.height)*ratio }
                            const text = `${o.tags[0].name} (${o.tags[0].confidence.toFixed(3)})`;
                            await this.drawRectangle(start, end);
                            await this.drawText(start, text);
                        }
                    }
                    // if(resp.people && resp.people.values.length){
                    //     for(const p of resp.people.values){
                    //         const start = {offsetX: p.boundingBox.x, offsetY: p.boundingBox.y }
                    //         const end = {offsetX: p.boundingBox.x + p.boundingBox.width, offsetY: p.boundingBox.y + p.boundingBox.height }
                    //         const text = `People: (${p.confidence.toFixed(3)})`;
                    //         await this.drawRectangle(start, end);
                    //         await this.drawText(start, text);
                    //     }
                    // }
                    if(resp.caption) {
                        const caption = resp.caption;
                        const text = `description: ${caption.text} (${caption.confidence.toFixed(3)})`
                        await this.drawText({offsetX:0, offsetY:0}, text)
                    }


                },
                error: (err) => {
                    NotificationService.popupError({msg: `analyze api error: ${err.message}`});
                }
            });
            //do the thinghy with the result
        }
    }

    private resizeWhiteboard(width: number, height: number){    
        if(this._canvasData){
            
            console.log(`resizeWhiteboard: image wxh (${this._canvasData.width}x${this._canvasData.height}) =>   into wxh (${width}x${height})`);

            //full image
            this.imageSizeRatio = {width: this._canvasData.width, height: this._canvasData.height, ratio: 1}

            if(width < this._canvasData.width || height < this._canvasData.height)
            {
                this.imageSizeRatio = AppHelper.resizeRectangleMaintainingAspectRatio(this._canvasData.width, this._canvasData.height, 
                    width, height);
            }

            this.canvasContainerWidth = this.imageSizeRatio.width; //-10;
            this.canvasContainerHeight = this.imageSizeRatio.height;// -10;
            // this.compositeZoom = this.imageSizeRatio.ratio;  
            this.updateOptions({ zoomBase: this.imageSizeRatio.ratio, 
                containerWidth: this.canvasContainerWidth, containerHeight: this.canvasContainerHeight,
                canvasWidth: this._canvasData.width, canvasHeight: this._canvasData.height });
            
            // NON applichiamo più lo zoom alle opzioni, solo visualmente tramite CSS transform
            //this.updateOptions({ zoom: 1 });

            console.log(`resizeWhiteboard: ${JSON.stringify(this.imageSizeRatio)}`);
        }
    }
        


    private async drawRectangle(start: any, end: any): Promise<void> {
        const rectElement = WhiteboardHelper.createRectangleElement(start, end, this.options);
        return this.addWhiteboardElement(rectElement);
    }
    private drawText(start: any, text: string): Promise<void>  {
        const textElement = WhiteboardHelper.createTextElement(start, text, this.options);
        return this.addWhiteboardElement(textElement);
    }
    private addWhiteboardElement(element: WhiteboardElement): Promise<void> {
        return Promise.resolve()
            .then(()=>{
                this.ngWhiteboardService.addElement(element);
            })
    }

    public updateOptions(options: Partial<typeof WhiteboardComponent.prototype.options>) {
        console.log(`[TB-DEBUG] updateOptions keys=[${Object.keys(options)}] zoomBase=${options.zoomBase?.toFixed(4) ?? '-'} containerW=${options.containerWidth ?? '-'} containerH=${options.containerHeight ?? '-'} undoSize=${this.undoRedoStackSizes.undoSize}`);
        this.options = { ...this.options, ...options };
    }
    
  colorChange(propName: ColorProperty, color: string) {
      console.log(`xyzw 🎨 colorChange chiamato - propName: ${propName}, color: ${color}`);
      console.log(`xyzw    isUpdatingColorFromSelection: ${this.isUpdatingColorFromSelection}`);
      console.log(`xyzw    selectedElements.length: ${this.selectedElements.length}`);

      if(!color) {
          console.log(`xyzw    ❌ Uscita: color vuoto`);
          return;
      }

      // Skip se il colore è già lo stesso nelle options (evita undo spurio da re-init template toolbar)
      if ((this.options as any)[propName] === color) {
          console.log(`[TB-DEBUG] colorChange SKIP: ${propName} già ${color}`);
          return;
      }

      // ✅ IGNORA se stiamo aggiornando da selezione
      if (this.isUpdatingColorFromSelection) {
          console.log(`xyzw    ⏭️ colorChange IGNORATO (update da selezione)`);
          return;
      }

      // ✅ Verifica se il colore è effettivamente diverso per gli elementi selezionati
      if (this.selectedElements.length > 0) {
          console.log(`xyzw    🔍 Verifica colori degli elementi selezionati...`);
          const allElementsHaveSameColor = this.selectedElements.every((element) => {
              const currentColor = element.type === ElementType.Text
                  ? element.style.color
                  : element.style.strokeColor;
              console.log(`xyzw       → element ${element.type} - ${element.id}: currentColor=${currentColor}, newColor=${color}, match=${currentColor === color}`);
              return currentColor === color;
          });

          console.log(`xyzw    🔍 allElementsHaveSameColor: ${allElementsHaveSameColor}`);

          if (allElementsHaveSameColor) {
              console.log(`xyzw    ⏭️ colorChange IGNORATO (tutti gli elementi hanno già questo colore: ${color})`);
              return;
          }
      }

      console.log(`xyzw    ✅ Applicando colore agli elementi...`);

      // Update selected elements only if there are any
      if (this.selectedElements.length > 0) {
          const style: any = { [propName]: color };
          console.log(`xyzw       → Aggiornando ${propName} a ${color} per ${this.selectedElements.length} elementi`);
          this.updateSelectedElement({ style });
      }

      this.updateOptions({ [propName]: color });
      this.config.setWhiteboardProperties( {strokeColor: color} );

      if (propName === 'strokeColor') {
          this.currentStrokeColor = color;
      }

      console.log(`xyzw    ✅ Colore applicato!`);
  }

    private updateSelectedElement(partialElement: Partial<WhiteboardElement>) {
        this.ngWhiteboardService.updateSelectedElements(partialElement);
    }

    private addCanvasImage(canvasImage: CanvasData | undefined) {
        this.addingImage = true;
        setTimeout(()=> {
            if (canvasImage) {
              console.log(`whiteboard addCanvasImage set wxh: ${canvasImage.width}x${canvasImage.height}`, canvasImage);
              this.ngWhiteboardService.addImage(canvasImage.image); // questo farà scattare onElementsAdded
              
              // Calcola il layout ottimale se abbiamo il container
            //   if (this.whiteboardContainer) {
            //       const rect = this.whiteboardContainer.nativeElement.getBoundingClientRect();
            //       this.calculateOptimalLayout(rect.width, rect.height);
            //   }
            }
        }) 
    }


    toggleRealtime() {
        if (this.isToggling) {
            return;
        }
        this.isToggling = true;
        this.config.setWhiteboardSharing( !this.config.whiteboardSharingEnabled );
        const val = this.sharedWhiteboardData(this.config.whiteboardSharingEnabled);
        
        if (this.config.whiteboardSharingEnabled) {
            this.sessionService.addWhiteboardImage(this.config.sessionId!, this._canvasData!).subscribe({
                next: (b: boolean) => {         
                    if (b) {
                        // this.alreadySent = true;
                        //NotificationService.showSuccess('Whiteboard image sent');
                    } else {
                        NotificationService.showError('Error during whiteboard image sharing');
                        this.config.setWhiteboardSharing(false);
                        
                    }
                    this.isToggling = false;
                    
                    this.store.dispatch(sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.EnableSharedWhiteboard, val, toIds: appActions.SEND_TO_ALL }));
                    if (this.config.whiteboardSharingEnabled) {
                       const elementsWithoutImage = this.config.whiteboardElements?.filter(z => z.type != ElementType.Image);
                        if(elementsWithoutImage?.length) {
                            const val = WhiteboardHelper.zipWhiteboardElements(elementsWithoutImage);
                            this.store.dispatch(sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.WhiteboardElements, val, toIds: appActions.SEND_TO_ALL }));
                        }
                        this.store.dispatch(sessionStatusActions.cmd_dispatch({ 
                            cmd: CommandEvent.WhiteboardRelativeTransform, 
                            val: this.config.whiteboardTransformData, 
                            toIds: appActions.SEND_TO_ALL}))
                    }
                },
                error: (err: any) => {
                    NotificationService.showError(`Error during whiteboard image sharing: ${err.message}`);
                    this.config.setWhiteboardSharing(false);
                    this.isToggling = false;
                }
            });           
        } else {
            this.store.dispatch(sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.EnableSharedWhiteboard, val, toIds: appActions.SEND_TO_ALL }));
         
            setTimeout(() => {
                this.isToggling = false;
            }, 800);
        }
    }
    
    private calculateOptimalLayout(containerWidth: number, containerHeight: number) {
        this.containerAspectRatio = containerWidth / containerHeight;

        if (!this._canvasData) {
            console.log(`Layout: aspect=${this.containerAspectRatio.toFixed(2)}, position=${this.toolbarPosition} (no image)`);
            return;
        }

        const imgW = this._canvasData.width;
        const imgH = this._canvasData.height;

        // Dimensioni wrapper stabili (host element: non cambiano con toolbar position)
        const hostEl = this.elementRef.nativeElement as HTMLElement;
        const wW = hostEl.clientWidth;
        const wH = hostEl.clientHeight;

        // Ricava altezza action bar dallo stato corrente
        // containerHeight = wH - [toolbar se top] - abH - 8 (il -8 viene da onResized)
        const abH = Math.max(0, this.toolbarPosition === 'left'
            ? wH - containerHeight - 8
            : wH - this.TB_TOP_H - containerHeight - 8);

        const calcFitArea = (availW: number, availH: number): number => {
            if (availW <= 0 || availH <= 0) return 0;
            const scale = Math.min(availW / imgW, availH / imgH, 1);
            return (imgW * scale) * (imgH * scale);
        };

        // Confronto: quale posizione dà più area all'immagine?
        const topArea = calcFitArea(wW - 8, wH - this.TB_TOP_H - abH - 8);
        const leftArea = calcFitArea(wW - this.TB_LEFT_W - 8, wH - abH - 8);

        const targetPosition: 'top' | 'left' = topArea >= leftArea ? 'top' : 'left';

        // Isteresi: cambia solo se il guadagno in area è significativo
        if (targetPosition !== this.toolbarPosition) {
            const currentArea = this.toolbarPosition === 'top' ? topArea : leftArea;
            const newArea = targetPosition === 'top' ? topArea : leftArea;
            const gain = currentArea > 0 ? (newArea - currentArea) / currentArea : 1;

            if (gain > this.AREA_GAIN_THRESHOLD) {
                this.applyToolbarTransition(targetPosition);
            }
        }

        console.log(`Layout: pos=${this.toolbarPosition}, wrapper=${wW}x${wH}, abH=${abH}, topArea=${topArea.toFixed(0)}, leftArea=${leftArea.toFixed(0)}`);
    }
    
    private applyToolbarTransition(newPosition: 'top' | 'left') {
        console.log(`[TB-DEBUG] applyToolbarTransition: ${this.toolbarPosition} → ${newPosition}, undoSize=${this.undoRedoStackSizes.undoSize}`);
        this.toolbarPosition = newPosition;
    }

    drawingProperties: DrawingProperties = this.config.whiteboardProperties || WhiteboardHelper.drawingPropertiesDefaults;
    currentStrokeColor: string = this.options.strokeColor || '#000000';

    onWhiteboardPropertiesChange(properties: DrawingProperties): void {
        // 1. Aggiorna solo gli elementi che hanno effettivamente valori diversi
        // Build a single style object to pass to the library for batch update
        if (this.selectedElements.length > 0) {
            const newFontSize = this.getFontSizeForViewBox(properties.fontSize, this.canvasData?.height || STANDARD_HEIGHT);
            const newDasharray = WhiteboardHelper.lineStyleToDashArray(properties.lineStyle, properties.strokeWidth);

            // Build style update object - the library will apply appropriate properties based on element type
            const style: any = {
                // Text properties
                fontFamily: properties.fontFamily,
                fontSize: newFontSize,
                fontStyle: properties.fontStyle,
                fontWeight: properties.fontWeight,
                // Shape properties
                strokeWidth: properties.strokeWidth,
                dasharray: newDasharray
            };

            console.log(`onWhiteboardPropertiesChange - Applicando modifiche a ${this.selectedElements.length} elementi`);
            this.updateSelectedElement({ style });
        }

        // 2. Aggiorna le options per i nuovi elementi
        this.updateOptions({
            fontFamily: properties.fontFamily,
            fontSize: this.getFontSizeForViewBox(properties.fontSize, this.canvasData?.height || STANDARD_HEIGHT),
            strokeWidth: properties.strokeWidth,
            dasharray: WhiteboardHelper.lineStyleToDashArray(properties.lineStyle, properties.strokeWidth),
            fontStyle: properties.fontStyle,
            fontWeight: properties.fontWeight
        });

        // 3. Aggiorna drawingProperties che sarà passato al child via @Input
        this.drawingProperties = { ...properties };

        this.config.setWhiteboardProperties(properties);
    }

    TakeControl(){
        this.isToggling = true;
        this.store.dispatch(sessionStatusActions.cmd_dispatch({ 
            cmd: CommandEvent.WhiteboardTakeControl, 
            val: this.config.partecipantId, 
            toIds:[this.config.whiteboardFromWho || '']}));
        setTimeout(()=>{this.isToggling = false}, 8000)
    }

    TakeControlResponse(b: boolean, who: string, inheritedUnsavedChanges?: boolean){
        this.config.setWhiteboardControlTaken(b, who);
        this.isToggling = false;

        // Se sto guadagnando il controllo, pauso la valutazione PRIMA di updateOptions
        // per evitare race condition con eventi asincroni della libreria
        if(b && who === this.config.partecipantId) {
            this.whiteboardInitialized = false;
        }

        this.updateOptions({drawingEnabled: !this.config.whiteboardReadonly});

        // Se ho guadagnato il controllo, gestisci unsavedChanges ereditato
        if(b && who === this.config.partecipantId) {
            console.log(`[WB-SAVE] TakeControlResponse - Controllo acquisito, inheritedUnsaved=${inheritedUnsavedChanges}, readonly=${this.config.whiteboardReadonly}, undoSize=${this.undoRedoStackSizes.undoSize}, savedUndoSize=${this.config.whiteboardSavedUndoSize}`);
            if (inheritedUnsavedChanges) {
                // L'owner precedente aveva modifiche non salvate → serve save esplicito
                this.config.setWhiteboardHasUnsavedChanges(true);
                // lastUndoSavedChanges resta -1 (sentinel)
                this.whiteboardInitialized = true;
                console.log(`[WB-SAVE] TakeControlResponse - inherited=true → unsaved=true, initialized=true`);
            } else {
                // L'owner precedente non aveva modifiche → cattura baseline dopo stabilizzazione
                this.config.setWhiteboardHasUnsavedChanges(false);
                this.pendingBaselineCapture = true;
                console.log(`[WB-SAVE] TakeControlResponse - inherited=false → unsaved=false, pendingBaselineCapture=true, waiting 300ms for baseline`);
                setTimeout(() => {
                    console.log(`[WB-SAVE] TakeControlResponse 300ms timer → markAsSaved(undoSize=${this.undoRedoStackSizes.undoSize}), pendingBaseline=${this.pendingBaselineCapture}`);
                    this.config.markWhiteboardAsSaved(this.undoRedoStackSizes.undoSize);
                    this.whiteboardInitialized = true;
                }, 300);
            }
        }

        // Se ho perso il controllo (b=true significa che qualcuno l'ha preso)
        // E quel qualcuno non sono io, deseleziona tutto
        if(b && who !== this.config.partecipantId) {
            console.log(`[WB-SAVE] TakeControlResponse - Perdita controllo`);
            this.ngWhiteboardService.clearSelection();
            this.selectedElements = [];
        }
    }

    ReplaceElements(els: WhiteboardElement[]){
        setTimeout(()=>this.fillWhiteboardData(els));
    }

    get undoDisabled(): boolean { 
        const elementCount = this.config.whiteboardElements?.length || 0;
        const zoomed = !!this.config.whiteboardTransformData?.selectionBounds;
        const elsTrans = this.config.whiteboardTransformData?.elementsTranslation
        const panned = elsTrans && (elsTrans.x != 0 || elsTrans.y != 0);
        return elementCount === 0 && !zoomed && !panned && this.undoRedoStackSizes.undoSize < 4;
    }
    get resetDisabled(): boolean { 
        const elementCount = this.config.whiteboardElements?.length || 0;
        const zoomed = !!this.config.whiteboardTransformData?.selectionBounds;
        const elsTrans = this.config.whiteboardTransformData?.elementsTranslation
        const panned = elsTrans && (elsTrans.x != 0 || elsTrans.y != 0);


//(config.whiteboard.elements?.length || 0) == 0 && !config.whiteboard.transformData && !canRedo        
        return elementCount === 0 && !zoomed && !panned && !this.undoRedoStackSizes.redoSize;
    }

    
    zoomIn(){
        if (this.config.whiteboardTransformData.zoomRelative < this.maxZoom) {
            this.ngWhiteboardService.applyZoom(1.2);
        }
    }
    zoomOut(){
        if(this.config.whiteboardTransformData.zoomRelative * 0.8 < 1) {
            this.ngWhiteboardService.setZoom(1);
        }
        else this.ngWhiteboardService.applyZoom(0.8);
    }

    onToolChange(): void {
        console.log('Tool changed to:', this.toolSelected.value);

        // Deseleziona tutti gli elementi quando cambi tool
        if (this.selectedElements.length > 0) {
            console.log(`onToolChange - Deselezione di ${this.selectedElements.length} elementi`);
            this.ngWhiteboardService.clearSelection();
            this.selectedElements = [];
        }

        // Quando cambi tool, resincronizza le properties visualizzate con le options correnti
        // così da "dimenticare" le proprietà dell'elemento precedentemente selezionato
        this.drawingProperties = {
            strokeColor: this.options.strokeColor,
            strokeWidth: this.options.strokeWidth || 5,
            lineStyle: WhiteboardHelper.getLineStyleFromDashArray(
                this.options.dasharray || '',
                this.options.strokeWidth || 5
            ),
            fontFamily: this.options.fontFamily || 'sans-serif',
            fontSize: this.options.fontSize || 24,
            fontStyle: this.options.fontStyle || 'normal',
            fontWeight: this.options.fontWeight || 'normal'
        };

        // Sincronizza anche il colore corrente
        this.currentStrokeColor = this.options.strokeColor || '#000000';
    }

}